/**
 * Speech recognition, behind an interface (TASKS T60, SPEC §2 `speech/recognizer.ts`).
 *
 * > Speech recognition and gamepad cannot be verified headlessly. Put both behind injectable
 * > interfaces, test against fakes, and list manual verification steps in BLOCKERS.md.
 *
 * So this module has three parts and only one of them can be tested by a machine:
 *
 *  - **`Recognizer`** — the contract. Start, stop, one callback per outcome. Deliberately smaller
 *    than the Web Speech API: no continuous mode, no interim results, no grammars. Every one of
 *    those is a source of behaviour that differs between Safari and Chrome, and none of them is
 *    needed to hear one short Spanish phrase.
 *  - **`BrowserRecognizer`** — the real thing, feature-detecting both `SpeechRecognition` and the
 *    `webkit` prefix. Untestable headlessly; its manual verification steps are in BLOCKERS.md.
 *  - **`FakeRecognizer`** — a scripted stand-in, which is what every test in this repository uses.
 *
 * ## Why the standalone check lives here as well as in `platform.ts`
 *
 * `SpeechRecognition` **exists** in an installed iOS web app and simply never returns a result — it
 * fires `start`, then nothing, or an opaque error. A learner who taps the mic and waits in silence
 * has no way to know what went wrong. So `createRecognizer` refuses to build a browser recognizer in
 * standalone mode at all, and the caller gets `null`, which the UI already renders as a one-line
 * notice to reopen in a Safari tab. Feature detection alone would be a lie on the one platform this
 * game is built for.
 */

/** What the microphone heard. */
export interface SpeechResult {
  /** Best transcript, as the engine spelled it. Never modified here — grading is `lang/`'s job. */
  transcript: string;
  /**
   * The engine's own confidence, 0..1, or null where it reported none.
   *
   * Recorded and never *scored*. Web Speech confidence is not comparable across engines, devices, or
   * even utterance lengths, and turning it into a mark would be exactly the sort of invented
   * measurement the tracker rules out elsewhere.
   */
  confidence: number | null;
}

export type SpeechFailure =
  /** The learner said nothing, or nothing the engine could segment. */
  | 'no-speech'
  /** Permission was refused, or the page is not in a secure context. */
  | 'not-allowed'
  /** The engine gave up: network, service, or an unnamed internal error. */
  | 'engine'
  /** Stopped by the app or by the learner letting go of push-to-talk. */
  | 'aborted';

export interface RecognizerHandlers {
  onResult(result: SpeechResult): void;
  /** Never a thrown error: a failed utterance is an ordinary event in this loop, not an exception. */
  onError(failure: SpeechFailure): void;
  /** Fired exactly once per `start()`, after `onResult` or `onError`. */
  onEnd?(): void;
}

export interface Recognizer {
  /** BCP-47. Mexican Spanish (BRIEF: never Peninsular). */
  readonly lang: string;
  /** True between `start()` and the end of that utterance. */
  listening(): boolean;
  start(handlers: RecognizerHandlers): void;
  /** Stop and take whatever was heard. Safe to call when not listening. */
  stop(): void;
  /** Stop and discard. Safe to call when not listening. */
  abort(): void;
}

/** es-MX, not es-ES and not es-419: the learner is learning Mexican Spanish (BRIEF <spanish>). */
export const SPEECH_LANG = 'es-MX';

// ---------------------------------------------------------------------------------------------
// The browser
// ---------------------------------------------------------------------------------------------

/** The slice of the Web Speech API used. Narrowed so nothing here depends on lib.dom's version. */
export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onend: (() => void) | null;
}

export type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

/** Both spellings, in the order a standards-compliant browser should win. */
export function speechRecognitionCtor(win: Window): SpeechRecognitionCtor | null {
  const w = win as unknown as Record<string, unknown>;
  for (const name of ['SpeechRecognition', 'webkitSpeechRecognition']) {
    if (typeof w[name] === 'function') return w[name] as SpeechRecognitionCtor;
  }
  return null;
}

/**
 * Map the engine's error code onto the four outcomes this app distinguishes.
 *
 * Four, because that is how many the *learner* can act on: say something, grant the microphone, try
 * again later, or nothing at all. A longer list would be a longer list of identical cards.
 */
export function classifyError(code: unknown): SpeechFailure {
  switch (code) {
    case 'no-speech':
    case 'audio-capture':
      return 'no-speech';
    case 'not-allowed':
    case 'service-not-allowed':
      return 'not-allowed';
    case 'aborted':
      return 'aborted';
    default:
      return 'engine';
  }
}

/** Pull the best transcript out of a `SpeechRecognitionEvent`, defensively. */
export function readResult(event: unknown): SpeechResult | null {
  const results = (event as { results?: ArrayLike<ArrayLike<unknown>> })?.results;
  if (!results || results.length === 0) return null;
  const alternatives = results[results.length - 1];
  if (!alternatives || alternatives.length === 0) return null;
  const best = alternatives[0] as { transcript?: unknown; confidence?: unknown };
  if (typeof best?.transcript !== 'string') return null;
  return {
    transcript: best.transcript.trim(),
    confidence: typeof best.confidence === 'number' ? best.confidence : null,
  };
}

/**
 * The real recognizer. **Cannot be verified headlessly** — see BLOCKERS.md.
 *
 * Configured for one short utterance: `continuous: false`, `interimResults: false`. Interim results
 * would let the card react while the learner is still speaking, which is a way to score somebody's
 * hesitation, and this app does not measure fluency (RESEARCH §5: no suprasegmental claims).
 */
export class BrowserRecognizer implements Recognizer {
  readonly lang: string;
  private engine: SpeechRecognitionLike | null = null;

  constructor(
    private readonly ctor: SpeechRecognitionCtor,
    lang: string = SPEECH_LANG,
  ) {
    this.lang = lang;
  }

  listening(): boolean {
    return this.engine !== null;
  }

  start(handlers: RecognizerHandlers): void {
    if (this.engine) return;
    const engine = new this.ctor();
    engine.lang = this.lang;
    engine.continuous = false;
    engine.interimResults = false;
    engine.maxAlternatives = 1;

    let settled = false;
    const finish = (): void => {
      this.engine = null;
      handlers.onEnd?.();
    };

    engine.onresult = (event) => {
      const result = readResult(event);
      settled = true;
      if (result) handlers.onResult(result);
      else handlers.onError('no-speech');
    };
    engine.onerror = (event) => {
      settled = true;
      handlers.onError(classifyError((event as { error?: unknown })?.error));
    };
    engine.onend = () => {
      // Some engines end without ever firing a result or an error — a silent utterance on iOS does
      // exactly this. Reported as `no-speech` rather than left hanging: a card waiting forever for
      // a callback that is not coming is the worst of the failure modes.
      if (!settled) handlers.onError('no-speech');
      finish();
    };

    this.engine = engine;
    try {
      engine.start();
    } catch {
      // `start()` throws if called twice, or where the page is not a secure context.
      this.engine = null;
      handlers.onError('engine');
      handlers.onEnd?.();
    }
  }

  stop(): void {
    this.engine?.stop();
  }

  abort(): void {
    this.engine?.abort();
  }
}

// ---------------------------------------------------------------------------------------------
// The fake
// ---------------------------------------------------------------------------------------------

/** One scripted utterance: what the microphone will "hear", or how it will fail. */
export type ScriptedUtterance =
  | { heard: string; confidence?: number }
  | { fails: SpeechFailure };

/**
 * A recognizer that hears what a test tells it to.
 *
 * Nothing is asynchronous unless a test asks for it: `say()` and `fail()` are called by the test at
 * the moment it chooses, so a speech test is as deterministic as a typed one.
 */
export class FakeRecognizer implements Recognizer {
  readonly lang: string;
  /** Every utterance it was asked to produce, in order. For assertions. */
  readonly heard: string[] = [];
  /** How many times `start()` was called. */
  starts = 0;
  private handlers: RecognizerHandlers | null = null;
  private readonly script: ScriptedUtterance[];

  constructor(script: readonly ScriptedUtterance[] = [], lang: string = SPEECH_LANG) {
    this.script = [...script];
    this.lang = lang;
  }

  listening(): boolean {
    return this.handlers !== null;
  }

  start(handlers: RecognizerHandlers): void {
    if (this.handlers) return;
    this.starts += 1;
    this.handlers = handlers;
    // A scripted recognizer answers on its own; an unscripted one waits for `say()`.
    const next = this.script.shift();
    if (next) this.deliver(next);
  }

  /** Hear a phrase now. */
  say(transcript: string, confidence: number | null = 0.9): void {
    this.deliver({ heard: transcript, ...(confidence === null ? {} : { confidence }) });
  }

  /** Fail now. */
  fail(failure: SpeechFailure): void {
    this.deliver({ fails: failure });
  }

  stop(): void {
    if (this.handlers) this.deliver({ fails: 'aborted' });
  }

  abort(): void {
    this.stop();
  }

  private deliver(utterance: ScriptedUtterance): void {
    const handlers = this.handlers;
    if (!handlers) return;
    this.handlers = null;
    if ('fails' in utterance) {
      handlers.onError(utterance.fails);
    } else {
      this.heard.push(utterance.heard);
      handlers.onResult({
        transcript: utterance.heard,
        confidence: utterance.confidence ?? null,
      });
    }
    handlers.onEnd?.();
  }
}

// ---------------------------------------------------------------------------------------------
// Choosing one
// ---------------------------------------------------------------------------------------------

export interface RecognizerEnvironment {
  /** From `detectStandalone`. */
  standalone: boolean;
  lang?: string;
}

/**
 * The real recognizer, or `null` where speech cannot work here.
 *
 * Null in two cases, and the second is the one that matters. Where the API is simply absent, the app
 * degrades to typed production silently (BRIEF). Where the page is **standalone**, the API is
 * present and *broken*: iOS fires `start` and then never returns a result, so a learner who taps the
 * mic waits in silence with nothing to read. Refusing to build one at all is what turns that into
 * the one-line "reopen in a Safari tab" notice the UI already shows.
 */
export function createRecognizer(win: Window, env: RecognizerEnvironment): Recognizer | null {
  if (env.standalone) return null;
  const ctor = speechRecognitionCtor(win);
  if (!ctor) return null;
  return new BrowserRecognizer(ctor, env.lang ?? SPEECH_LANG);
}
